import React, { useCallback } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { ServerSideDatatable } from '../serverside-datatable';
import { KitFilterer } from './kit-filterer';
import useAlertStore from '../../store/alert';
import { useAppContext } from '../../App';
import { Button } from '../inputs/button';
import { useKitStore } from '../../store/kit-store';

interface Props {}

export const KitList: React.FC<Props> = () => {
  const {
    currentPage,
    loadingKits,
    kits,
    listKits,
    deleteKit,
    deleteKitStatus,
    totalCount,
  } = useKitStore();

  const { alert } = useAlertStore();
  const { globalNotif } = useAppContext();
  const navigate = useNavigate();

  const deleteRetailKitConfirm = useCallback(
    async (id: number) => {
      alert(
        'Delete Kit',
        `Are you sure you want to permanently delete this kit?

        <i>Note: This cannot be undone. Once deleted, it cannot be recovered.</i>`,
        [
          { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
          {
            text: 'Yes',
            onClick: async () => {
              if (id) {
                await deleteKit({ id });
                globalNotif?.success({
                  message: `Order successfully deleted.`,
                });
                navigate(0);
              }
            },
          },
        ],
      );
    },
    [alert, deleteKit, globalNotif, navigate],
  );

  return (
    <ServerSideDatatable
      defaultOrderReference="createdAt"
      searchBarPlaceholder="Search by Kit ID"
      columns={[
        {
          id: 'uniqueId',
          name: (
            <h5 className="text-gray font-medium text-base">Unique Kit ID</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <span className="text-sm font-medium text-blue hover:underline">
              {row.uniqueId}
            </span>
          ),
          grow: 2.5,
          sortable: true,
          sortField: 'uniqueId',
        },
        {
          id: 'retailer',
          name: <h5 className="text-gray font-medium text-base">Retailer</h5>,
          selector: data => data.retailer,
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => <span>{row.retailer}</span>,
          grow: 2,
          sortable: true,
          sortField: 'retailer',
        },
        {
          id: 'createdBy',
          name: (
            <h5 className="text-gray font-medium text-base">Uploaded By</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <span>
              {row.createdByUser.firstName} {row.createdByUser.lastName}
            </span>
          ),
          grow: 4,
          sortable: true,
          sortField: 'createdBy',
        },

        {
          id: 'createdAt',
          name: (
            <h5 className="text-gray font-medium text-base">Uploaded Date</h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-col font-medium text-sm">
              <div>
                {DateTime.fromISO(row.createdAt).toFormat('LL-dd-yyyy')}
              </div>
              <div>{DateTime.fromISO(row.createdAt).toFormat('hh:mm a')}</div>
            </div>
          ),
          grow: 2.5,
          sortable: true,
          sortField: 'createdAt',
        },
        {
          id: 'actions',
          name: '',
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <Button
              label="Delete"
              colorClassname="bg-gray-200 text-black"
              className="px-5 py-2 rounded-md hover:bg-gray-300"
              onClick={() => deleteRetailKitConfirm(row.kitId)}
              loading={deleteKitStatus.isLoading}
            />
          ),
          grow: 1.5,
        },
      ]}
      data={kits}
      currentPage={currentPage}
      paginationTotalRows={totalCount}
      progressPending={loadingKits}
      serverAction={listKits}
      filterComponent={<KitFilterer />}
    />
  );
};
